import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = {
  key: 0,
  class: "rounded-lg border border-yellow-200 bg-yellow-50 p-4"
}
const _hoisted_3 = { class: "rounded-lg border border-gray-200 bg-white p-3 sm:p-6" }
const _hoisted_4 = { class: "rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4" }
const _hoisted_5 = { class: "mb-2 text-xs font-medium text-black" }
const _hoisted_6 = { class: "space-y-4" }
const _hoisted_7 = { class: "mb-3 flex items-center justify-between" }
const _hoisted_8 = { class: "font-medium text-black" }
const _hoisted_9 = ["disabled", "onClick"]
const _hoisted_10 = { class: "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" }
const _hoisted_11 = { class: "text-sm text-black" }
const _hoisted_12 = ["onUpdate:modelValue", "disabled"]
const _hoisted_13 = { class: "text-sm text-black" }
const _hoisted_14 = ["onUpdate:modelValue", "disabled"]
const _hoisted_15 = { class: "text-sm text-black" }
const _hoisted_16 = ["onUpdate:modelValue", "disabled"]
const _hoisted_17 = { class: "text-sm text-black" }
const _hoisted_18 = ["onUpdate:modelValue", "disabled"]
const _hoisted_19 = {
  key: 0,
  class: "mt-3 text-sm font-medium text-red-600"
}
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "flex flex-col items-stretch gap-2" }
const _hoisted_22 = ["disabled"]
const _hoisted_23 = {
  key: 0,
  class: "fas fa-spinner fa-spin mr-1"
}
const _hoisted_24 = {
  key: 0,
  class: "text-center text-xs text-red-600"
}

import { computed, ref, watch } from "vue";
import ModalComp from "@/components/ui/ModalComp.vue";
import ParcelWeightWarning from "@/components/order/ParcelWeightWarning.vue";
import UploadZone from "@/components/order/UploadZone.vue";
import { removeParcelDimension } from "@/api/orders";
import type { ParcelDimension } from "@/types";
import {
  getCarrierDimensionLimits,
  getDimensionLimitsHint,
  validateColisDimensions,
} from "@/utils/parcelDimensionsLimits";


export default /*@__PURE__*/_defineComponent({
  __name: 'ConfirmParcelDimensionsModal',
  props: {
    isVisible: { type: Boolean },
    orderId: {},
    initialDimensions: {},
    calculatedWeight: {},
    idLivraison: {},
    countryCode: {},
    postalCode: {},
    loading: { type: Boolean },
    requireInvoice: { type: Boolean, default: false },
    hasInvoice: { type: Boolean, default: false },
    orderGroupId: {},
    orderDate: {},
    invoiceId: {}
  },
  emits: ["update:isVisible", "confirm", "removed", "remove-error", "invoice-uploaded"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const editableDimensions = ref<ParcelDimension[]>([]);
const isRemoving = ref(false);

const cloneDimensions = (list: ParcelDimension[]): ParcelDimension[] =>
  list.map((colis) => ({
    longueur: colis.longueur,
    largeur: colis.largeur,
    hauteur: colis.hauteur,
    poids: colis.poids,
  }));

watch(
  () => props.isVisible,
  (visible) => {
    if (!visible) return;
    editableDimensions.value =
      props.initialDimensions.length > 0
        ? cloneDimensions(props.initialDimensions)
        : [{ longueur: null, largeur: null, hauteur: null, poids: null }];
  },
);

const dimensionLimitsHint = computed(() =>
  getDimensionLimitsHint(props.idLivraison ?? null, props.countryCode, props.postalCode),
);

const dimensionLimits = computed(() =>
  getCarrierDimensionLimits(props.idLivraison ?? null, props.countryCode, props.postalCode),
);

const parcelError = (colis: ParcelDimension): string => {
  const { longueur, largeur, hauteur, poids } = colis;
  if (longueur == null || largeur == null || hauteur == null || poids == null)
    return "Toutes les dimensions et le poids sont obligatoires.";
  if (longueur <= 0 || largeur <= 0 || hauteur <= 0 || poids <= 0)
    return "Toutes les dimensions et le poids doivent être supérieurs à zéro.";
  const dimensionsError = validateColisDimensions(
    longueur,
    largeur,
    hauteur,
    dimensionLimits.value,
  );
  if (dimensionsError) return `Gabarit dépassé : ${dimensionsError}.`;
  if (poids > dimensionLimits.value.poids)
    return `Poids ${poids} kg (maximum ${dimensionLimits.value.poids} kg).`;
  return "";
};

const canConfirm = computed(
  () =>
    editableDimensions.value.length > 0 &&
    editableDimensions.value.every((colis) => !parcelError(colis)) &&
    (!props.requireInvoice || props.hasInvoice),
);

const addParcel = () => {
  editableDimensions.value.push({
    longueur: null,
    largeur: null,
    hauteur: null,
    poids: null,
  });
};

const removeParcel = async (index: number) => {
  if (editableDimensions.value.length <= 1 || isRemoving.value) return;
  isRemoving.value = true;
  try {
    await removeParcelDimension(props.orderId, index);
    editableDimensions.value.splice(index, 1);
    emit("removed");
  } catch (error) {
    emit(
      "remove-error",
      error instanceof Error ? error.message : "Erreur lors de la suppression",
    );
  } finally {
    isRemoving.value = false;
  }
};

const handleConfirm = () => {
  if (!canConfirm.value || props.loading) return;
  emit("confirm", cloneDimensions(editableDimensions.value));
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalComp, {
    "is-visible": _ctx.isVisible,
    title: "Téléchargement des étiquettes de groupage",
    size: "xl",
    "hide-footer": true,
    "onUpdate:isVisible": _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('update:isVisible', $event)))
  }, {
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (_ctx.requireInvoice && !_ctx.hasInvoice)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _cache[2] || (_cache[2] = _createElementVNode("h5", { class: "mb-3 text-sm font-semibold text-red-600" }, [
                _createElementVNode("i", { class: "fas fa-exclamation-triangle mr-2" }),
                _createTextVNode(" Facture requise ")
              ], -1)),
              _createVNode(UploadZone, {
                "order-id": _ctx.orderId,
                "order-group-id": _ctx.orderGroupId || 0,
                "order-date": _ctx.orderDate || new Date().toISOString(),
                "invoice-id": _ctx.invoiceId,
                onInvoiceUploaded: _cache[0] || (_cache[0] = () => emit('invoice-uploaded'))
              }, null, 8, ["order-id", "order-group-id", "order-date", "invoice-id"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_3, [
          _cache[16] || (_cache[16] = _createElementVNode("h4", { class: "mb-4 font-bold text-black" }, "Préparation de l’expédition", -1)),
          _createElementVNode("div", _hoisted_4, [
            _cache[14] || (_cache[14] = _createElementVNode("h5", { class: "mb-4 font-semibold text-black" }, [
              _createElementVNode("i", { class: "fas fa-ruler-combined mr-2 text-blue-600" }),
              _createTextVNode(" Dimensions des colis ")
            ], -1)),
            _cache[15] || (_cache[15] = _createElementVNode("p", { class: "mb-4 text-sm text-black" }, " Veuillez renseigner les dimensions en centimètres (cm) et le poids pour chaque colis. Tous les champs sont obligatoires. Si un colis dépasse le gabarit indiqué, répartissez son contenu dans deux colis ou plus. ", -1)),
            _createElementVNode("p", _hoisted_5, _toDisplayString(dimensionLimitsHint.value), 1),
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(editableDimensions.value, (colis, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: "rounded-lg border border-gray-300 bg-white p-3 sm:p-4"
                }, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("span", _hoisted_8, "Colis n°" + _toDisplayString(index + 1), 1),
                    (editableDimensions.value.length > 1)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          type: "button",
                          class: "text-sm text-red-600 hover:text-red-700",
                          disabled: _ctx.loading || isRemoving.value,
                          onClick: ($event: any) => (removeParcel(index))
                        }, _cache[3] || (_cache[3] = [
                          _createElementVNode("i", { class: "fas fa-trash-alt mr-1" }, null, -1),
                          _createTextVNode("Supprimer ")
                        ]), 8, _hoisted_9))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("label", _hoisted_11, [
                      _cache[4] || (_cache[4] = _createTextVNode(" Longueur (cm) ")),
                      _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-red-600" }, "*", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((colis.longueur) = $event),
                        type: "number",
                        min: "0",
                        step: "0.1",
                        disabled: _ctx.loading || isRemoving.value,
                        class: "mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                      }, null, 8, _hoisted_12), [
                        [
                          _vModelText,
                          colis.longueur,
                          void 0,
                          { number: true }
                        ]
                      ])
                    ]),
                    _createElementVNode("label", _hoisted_13, [
                      _cache[6] || (_cache[6] = _createTextVNode(" Largeur (cm) ")),
                      _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-red-600" }, "*", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((colis.largeur) = $event),
                        type: "number",
                        min: "0",
                        step: "0.1",
                        disabled: _ctx.loading || isRemoving.value,
                        class: "mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                      }, null, 8, _hoisted_14), [
                        [
                          _vModelText,
                          colis.largeur,
                          void 0,
                          { number: true }
                        ]
                      ])
                    ]),
                    _createElementVNode("label", _hoisted_15, [
                      _cache[8] || (_cache[8] = _createTextVNode(" Hauteur (cm) ")),
                      _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-red-600" }, "*", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((colis.hauteur) = $event),
                        type: "number",
                        min: "0",
                        step: "0.1",
                        disabled: _ctx.loading || isRemoving.value,
                        class: "mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                      }, null, 8, _hoisted_16), [
                        [
                          _vModelText,
                          colis.hauteur,
                          void 0,
                          { number: true }
                        ]
                      ])
                    ]),
                    _createElementVNode("label", _hoisted_17, [
                      _cache[10] || (_cache[10] = _createTextVNode(" Poids (kg) ")),
                      _cache[11] || (_cache[11] = _createElementVNode("span", { class: "text-red-600" }, "*", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((colis.poids) = $event),
                        type: "number",
                        min: "0",
                        step: "0.01",
                        disabled: _ctx.loading || isRemoving.value,
                        class: "mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                      }, null, 8, _hoisted_18), [
                        [
                          _vModelText,
                          colis.poids,
                          void 0,
                          { number: true }
                        ]
                      ])
                    ])
                  ]),
                  (parcelError(colis))
                    ? (_openBlock(), _createElementBlock("p", _hoisted_19, [
                        _cache[12] || (_cache[12] = _createElementVNode("i", { class: "fas fa-exclamation-circle mr-1" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(parcelError(colis)), 1)
                      ]))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ]),
            _createVNode(ParcelWeightWarning, {
              "show-comparison": "",
              "calculated-weight": _ctx.calculatedWeight,
              parcels: editableDimensions.value
            }, null, 8, ["calculated-weight", "parcels"]),
            _createElementVNode("button", {
              type: "button",
              class: "mt-4 px-4 py-2 border border-blue-500 rounded-md text-sm font-medium text-blue-600 hover:border-blue-700 hover:text-blue-700 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed",
              disabled: _ctx.loading || isRemoving.value,
              onClick: addParcel
            }, _cache[13] || (_cache[13] = [
              _createElementVNode("i", { class: "fas fa-plus mr-2" }, null, -1),
              _createTextVNode("Ajouter un colis ")
            ]), 8, _hoisted_20)
          ])
        ]),
        _cache[18] || (_cache[18] = _createElementVNode("div", { class: "rounded-lg border border-amber-200 bg-amber-50 p-3" }, [
          _createElementVNode("p", { class: "text-sm text-amber-900" }, [
            _createElementVNode("i", { class: "fas fa-exclamation-triangle mr-2" }),
            _createElementVNode("strong", null, "Attention :"),
            _createTextVNode(" En cas de contrôle transporteur, si les dimensions ou le poids renseignés ne correspondent pas aux valeurs réelles et qu’une surfacturation nous est appliquée, celle-ci sera à votre charge. ")
          ])
        ], -1)),
        _createElementVNode("div", _hoisted_21, [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["w-full rounded-lg px-6 py-3 text-base font-semibold transition-colors disabled:cursor-not-allowed", canConfirm.value ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-400 text-black']),
            disabled: !canConfirm.value || _ctx.loading || isRemoving.value,
            onClick: handleConfirm
          }, [
            (_ctx.loading)
              ? (_openBlock(), _createElementBlock("i", _hoisted_23))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(_ctx.loading ? "Enregistrement..." : "Confirmer et télécharger"), 1)
          ], 10, _hoisted_22),
          (_ctx.requireInvoice && !_ctx.hasInvoice)
            ? (_openBlock(), _createElementBlock("p", _hoisted_24, _cache[17] || (_cache[17] = [
                _createElementVNode("i", { class: "fas fa-exclamation-triangle mr-1" }, null, -1),
                _createTextVNode("Veuillez d'abord uploader une facture ")
              ])))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }, 8, ["is-visible"]))
}
}

})